import * as E from 'fp-ts/Either';
import React, { useEffect } from 'react';
import { login } from '../api/auth';
import { single } from '../api/users';

type User = {
  _id: string;
  username: string;
  roles: string[];
};

type AuthSession = {
  token: string | null;
  user: User | null;
  expiresAt: string | null;
};

export interface AuthContextType {
  user: User | null;
  validating: boolean;
  token: string | null;
  error: string | null;
  signin: (user: string, password: string, success: VoidFunction) => void;
  signout: (callback: VoidFunction) => void;
}

let AuthContext = React.createContext<AuthContextType | null>(null);

const validateSession = (mixed: unknown): AuthSession | null => {
  try {
    const session = typeof mixed === 'string' ? JSON.parse(mixed) : mixed;
    if (typeof session !== 'object' || session === null) {
      return null;
    }
    if (typeof session.token !== 'string' || session.token === '') {
      return null;
    }
    if (typeof session.user !== 'object' || session.user === null) {
      return null;
    }
    if (typeof session.expiresAt !== 'string' || session.expiresAt === '') {
      return null;
    }
    return session as AuthSession;
  } catch (e) {
    return null;
  }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [validating, setValidating] = React.useState<boolean>(false);
  const [session, _setSession] = React.useState<AuthSession | null>(
    validateSession(window.localStorage.getItem('session'))
  );
  const [error, setError] = React.useState<string | null>(null);

  const setSession = (session: AuthSession | null) => {
    _setSession(session);
    if (session) {
      window.localStorage.setItem('session', JSON.stringify(session));
    } else {
      window.localStorage.removeItem('session');
    }
  };

  useEffect(() => {
    const cancel = window.setInterval(() => {
      if (!session) {
        return;
      }
      if (session.expiresAt && new Date(session.expiresAt) < new Date()) {
        setSession(null);
        setError('Session expired. Please log in again.');
      }
    }, 1000);

    return () => window.clearInterval(cancel);
  });

  let signin = async (
    username: string,
    password: string,
    success: VoidFunction
  ) => {
    setError(null);
    setValidating(true);
    const res = await login(username, password);
    if (E.isLeft(res)) {
      setSession(null);
      setValidating(false);
      setError(res.left.helpMessage ?? res.left.message ?? 'Failed to login');
      return;
    }

    const { token, userId, expiresAt } = res.right;

    const userRes = await single(token, userId);
    if (E.isLeft(userRes)) {
      setSession(null);
      setValidating(false);
      setError(
        userRes.left.helpMessage ?? userRes.left.message ?? 'Failed to login'
      );
      return;
    }

    const user = userRes.right;

    setSession({ token, user, expiresAt });
    setValidating(false);

    success();
  };

  let signout = async (callback: VoidFunction) => {
    setSession(null);
    setError(null);
    callback();
  };

  let value = {
    error,
    user: session?.user ?? null,
    token: session?.token ?? null,
    validating,
    signin,
    signout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return React.useContext(AuthContext);
};
